import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';

// Define the API endpoint
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '';

const FacebookCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Parse query parameters
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const state = searchParams.get('state');

      if (error) {
        console.error('Facebook authentication error:', error);
        setError(`Facebook authentication error: ${error}`);
        setLoading(false);
        return;
      }

      if (!code) {
        console.error('No code parameter found in callback URL');
        setError('No code parameter found in callback URL');
        setLoading(false);
        return;
      }

      // Get the current authenticated session
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        console.error('No authentication token available');
        setError('Authentication error. Please sign in again.');
        setLoading(false);
        return;
      }

      // Get the redirect URI
      const redirectUri = window.location.origin + '/facebook-callback';

      // Handle Facebook authentication callback
      const response = await fetch(`${API_ENDPOINT}/facebook/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({
          code,
          redirect_uri: redirectUri,
          state
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP error! status: ${response.status}`, errorText);
        throw new Error(`Error handling Facebook authentication callback: ${errorText}`);
      }

      // Authentication successful
      setSuccess(true);
      
      // Close the window after a short delay
      setTimeout(() => {
        window.close();
      }, 2000);
    } catch (error) {
      console.error('Error handling Facebook authentication callback:', error);
      setError(`Error handling Facebook authentication callback: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Paper sx={{ p: 4, maxWidth: 500 }}>
        <Typography variant="h5" gutterBottom>
          Facebook Authentication
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>Processing authentication...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : success ? (
          <Alert severity="success" sx={{ my: 2 }}>
            Successfully authenticated with Facebook. This window will close automatically.
          </Alert>
        ) : null}
      </Paper>
    </Box>
  );
};

export default FacebookCallback;
